import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import { useAuth } from '../context/AuthContext';
import { getAccount, updateAccount } from '../lib/api';

const emptyAddress = {
  label: 'Delivery',
  fullName: '',
  phone: '',
  address: '',
  deliveryNotes: '',
};

function AddressesPage() {
  const { user, isAuthenticated, updateUser } = useAuth();
  const [address, setAddress] = useState(emptyAddress);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadAccount() {
      try {
        const response = await getAccount({ userId: user.id });
        if (!isMounted) {
          return;
        }

        const savedAddress = response.user.addressBook?.[0] || {};
        setAddress({
          ...emptyAddress,
          ...savedAddress,
          address: savedAddress.address || savedAddress.fullAddress || savedAddress.addressLine1 || '',
          deliveryNotes: savedAddress.deliveryNotes || '',
        });
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    if (user?.id) {
      loadAccount();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: '/account/addresses' }} />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await updateAccount({
        userId: user.id,
        updates: { addressBook: [address] },
      });
      updateUser(response.user);
      setNotice('Address saved.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="page-card account-page">
      <div className="account-page__header">
        <div>
          <p className="profile-page__eyebrow">Address Book</p>
          <h1 className="page-card__title">Manage your saved delivery address.</h1>
          <p className="page-card__text">
            Keep one simple delivery address ready for faster checkout.
          </p>
        </div>
        <div className="account-page__tabs">
          <Link to="/profile">Profile</Link>
          <Link to="/orders">Orders</Link>
          <Link to="/account/addresses" className="is-active">Addresses</Link>
          <Link to="/account/settings">Settings</Link>
        </div>
      </div>

      {isLoading ? <p className="products-state">Loading address book...</p> : null}
      {error ? <p className="products-state products-state--error">{error}</p> : null}
      {notice ? <p className="orders-success">{notice}</p> : null}

      <form className="auth-form account-form" onSubmit={handleSubmit}>
        <label className="auth-form__field">
          <span>Label</span>
          <input value={address.label} onChange={(event) => setAddress((current) => ({ ...current, label: event.target.value }))} />
        </label>
        <label className="auth-form__field">
          <span>Full Name</span>
          <input value={address.fullName} onChange={(event) => setAddress((current) => ({ ...current, fullName: event.target.value }))} />
        </label>
        <label className="auth-form__field">
          <span>Phone</span>
          <input value={address.phone} onChange={(event) => setAddress((current) => ({ ...current, phone: event.target.value }))} />
        </label>
        <label className="auth-form__field">
          <span>Address</span>
          <textarea
            rows="3"
            value={address.address}
            onChange={(event) => setAddress((current) => ({ ...current, address: event.target.value }))}
          />
        </label>
        <label className="auth-form__field">
          <span>Delivery Notes</span>
          <textarea
            rows="3"
            value={address.deliveryNotes}
            onChange={(event) => setAddress((current) => ({ ...current, deliveryNotes: event.target.value }))}
          />
        </label>
        <GreenButton disabled={isSaving} type="submit" text={isSaving ? 'Saving...' : 'Save Address'} />
      </form>
    </section>
  );
}

export default AddressesPage;
